import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useValidationStore } from "@/state/validationState";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";

/**
 * The scoped final safety-questioning action (#851) and the runtime-detection
 * state a frozen candidate has against the Project's target (#853).
 *
 * Aliases into the generated contract, like every other api module: change the
 * contract, regenerate, and these names either still resolve or fail to
 * compile.
 */
export type ProjectFinalQuestioning =
  components["schemas"]["ProjectFinalQuestioning"];
export type ReplaceProjectFinalQuestioningRequest =
  components["schemas"]["ReplaceProjectFinalQuestioningRequest"];
export type FinalQuestioningState =
  components["schemas"]["FinalQuestioningState"];
export type FinalQuestioningStaleReason =
  components["schemas"]["FinalQuestioningStaleReason"];
export type CandidateFinalQuestioning =
  components["schemas"]["CandidateFinalQuestioning"];
export type CandidateRuntimeDetection =
  components["schemas"]["CandidateRuntimeDetection"];

/**
 * Whether this Project's interview runs its final safety questioning.
 *
 * `effective`, never `state`: `inherited` and `enabled` are different
 * decisions and the same behaviour, and a surface that warned on one of them
 * would be warning about an authoring choice rather than about the interview.
 * The server resolves inheritance once (`effective`) so no consumer re-derives
 * it — the whole reason the field exists.
 */
export function isFinalQuestioningOff(
  state: ProjectFinalQuestioning | undefined,
): boolean {
  return state?.effective === "disabled";
}

/**
 * Whether the retained disable confirmation still covers the current draft
 * revision and the consequence wording this deployment serves.
 *
 * Derived server-side and read here, never recomputed: contract §8 makes a
 * moved draft or a revised contract invalidate the acknowledgment, and the
 * freeze/validation lane refuses on the SAME reading
 * (`safety.final_questioning_evidence_stale`). Two derivations would be two
 * chances to tell a clinician their evidence holds while the service refuses
 * it.
 */
export function hasStaleAcknowledgment(
  state: ProjectFinalQuestioning | undefined,
): boolean {
  return state?.acknowledgment?.status === "stale";
}

/** Read the Project's final safety-questioning state and its evidence (#851). */
export function useFinalQuestioningQuery(
  projectId: string,
): UseQueryResult<ProjectFinalQuestioning> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.projectFinalQuestioning(projectId),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/final-questioning", {
          params: { path: { projectId } },
          signal,
        }),
      );
    },
    enabled: projectId.length > 0,
  });
}

/**
 * Set the state under all three of its compare-and-set tokens.
 *
 * The caller supplies the tokens it actually read — the contract revision it
 * displayed consequences for, the state version it decided against, and the
 * draft revision PAIR the choice binds to. This hook never re-reads one at
 * submit time: refreshing a token behind the clinician is how a stale refusal
 * becomes a lost update, and the CAS exists to refuse instead.
 *
 * CANCEL NEEDS NO CODE HERE. An unsent request changes nothing, and no state
 * is inferred from a partial call — which is why the dialog's Cancel does not
 * call this at all rather than sending a "no change" write.
 */
export function useReplaceFinalQuestioningMutation(
  projectId: string,
): UseMutationResult<
  ProjectFinalQuestioning,
  unknown,
  ReplaceProjectFinalQuestioningRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const clearValidation = useValidationStore((state) => state.clear);

  return useMutation({
    async mutationFn(body) {
      return unwrap(
        await client.PUT("/v1/projects/{projectId}/final-questioning", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    async onSuccess(saved) {
      queryClient.setQueryData(
        queryKeys.projectFinalQuestioning(projectId),
        saved,
      );
      await settleFinalQuestioningWrite(queryClient, projectId, clearValidation);
    },
    onError(error) {
      const failure = classifyFinalQuestioningWriteFailure(error);
      // EVERY refusal that disproves a token this request carried re-reads the
      // resource, and that set is three, not one.
      //
      // The dialog goes terminal on all three and tells the clinician to close
      // and reopen. Reopening captures the parent query's tokens again — so if
      // the refusal did not refresh them, the new dialog opens on the exact
      // tokens the server has just rejected and the next attempt fails
      // identically. That is a dead end on the SAFETY-RESTORING direction most
      // of all: a colleague's draft save can refuse a re-enable, and the
      // instructed recovery has to actually work.
      //
      // `stale_state` — someone moved the posture; `stale_draft` — someone
      // moved the draft the confirmation would bind to; `contract_revised` —
      // this deployment states different consequences than the ones displayed.
      // A fresh read answers all three, and none of them is ever resent
      // automatically.
      const tokensDisproved =
        failure.kind === "stale_state" ||
        failure.kind === "stale_draft" ||
        failure.kind === "contract_revised";
      if (!tokensDisproved && failure.kind !== "ambiguous") {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectFinalQuestioning(projectId),
      });
      // The WIDER settlement is narrower than that set. `stale_state` and
      // `stale_draft` are news that the effective configuration moved, and an
      // `ambiguous` failure may have committed before the reply was lost — so
      // the validated badge, the Overview and the candidates' freshness all
      // describe something that no longer holds. `contract_revised` moved
      // nothing about the Project: only the wording this deployment serves
      // changed, and clearing a valid validation report over it would be a
      // refusal invented by the browser.
      if (failure.kind === "contract_revised") {
        return;
      }
      void settleFinalQuestioningWrite(queryClient, projectId, clearValidation);
    },
    retry: false,
  });
}

/**
 * What every mounted reader has to re-observe after the posture moved.
 *
 * More than the usual set, because the SERVER clears state in the same
 * transaction as this write: `replace_project_final_questioning` nulls the
 * draft's validated badge and deletes the virtual verdict, since the posture
 * is projected into the tree a verdict describes. A browser that kept its
 * cached badge would keep the candidate gate enabled off a verdict the service
 * has already discarded.
 *
 * The candidate listing is invalidated for the freshness half: a posture
 * transition moves the Project's `finalQuestioningStateVersion`, which is one
 * of the coordinates a frozen candidate's server-derived freshness compares
 * (`final_questioning_changed`). Leaving it cached would keep the Preview
 * modal calling a superseded candidate "Current".
 *
 * Awaited rather than fire-and-forget, as the adoption and response-filtering
 * writers are: the dialog closes when the mutation resolves, and a
 * fire-and-forget invalidation lets it close over the previous reading for a
 * visible interval.
 */
async function settleFinalQuestioningWrite(
  queryClient: QueryClient,
  projectId: string,
  clearValidation: (projectId: string) => void,
): Promise<void> {
  clearValidation(projectId);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.draft(projectId) }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.projectOverview(projectId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.previewCandidates(projectId),
    }),
  ]);
}

export type FinalQuestioningWriteFailure =
  /** Somebody else moved the Project's final-questioning state first. */
  | { kind: "stale_state"; currentStateVersion: string | null }
  /** The draft moved, so the confirmation would bind to a revision that is gone. */
  | { kind: "stale_draft" }
  /** This deployment states different consequences than the ones displayed. */
  | { kind: "contract_revised"; currentContractRevision: string | null }
  | { kind: "denied" }
  | { kind: "invalid"; message: string; requestId: string | undefined }
  | { kind: "ambiguous"; requestId: string | undefined }
  | { kind: "error"; code: string; requestId: string | undefined };

function envelopeString(error: ApiError, key: string): string | null {
  const value = error.envelope[key];
  return typeof value === "string" ? value : null;
}

/**
 * The four refusals this action can produce, told apart because the recoveries
 * differ and none of them changed any state.
 *
 * `stale_draft` is NOT collapsed onto `stale_state`: one is recovered by
 * re-reading the Project's own decision and deciding again, the other by
 * re-reading a draft somebody else saved. Telling a clinician to do the wrong
 * one is how a re-enable gets abandoned.
 */
export function classifyFinalQuestioningWriteFailure(
  error: unknown,
): FinalQuestioningWriteFailure {
  if (!(error instanceof ApiError)) {
    return { kind: "ambiguous", requestId: undefined };
  }
  if (error.code === "stale_final_questioning_state_version") {
    return {
      kind: "stale_state",
      currentStateVersion: envelopeString(
        error,
        "currentFinalQuestioningStateVersion",
      ),
    };
  }
  if (error.code === "stale_draft_version") {
    return { kind: "stale_draft" };
  }
  if (error.code === "final_questioning_contract_revision_mismatch") {
    return {
      kind: "contract_revised",
      currentContractRevision: envelopeString(
        error,
        "currentFinalQuestioningContractRevision",
      ),
    };
  }
  if (error.isForbidden || error.isUnauthenticated || error.status === 404) {
    return { kind: "denied" };
  }
  if (error.status >= 500 && !error.isNotImplemented) {
    return { kind: "ambiguous", requestId: error.requestId };
  }
  // A WORDING change without a revision bump, which the service supports on
  // purpose: `_acknowledgment_status` compares the digest FIRST precisely so
  // edited consequences cannot keep reading as confirmed under an unchanged
  // label. The write then refuses 422 on `acknowledgment.consequencesDigest`
  // while `expectedContractRevision` still matches — so without this arm the
  // refusal classified as an ordinary `invalid`, stayed non-terminal, refreshed
  // nothing, and every retry resent the same stale digest for the same refusal
  // (claude review round 3).
  //
  // Reported as `contract_revised` rather than a fourth kind: the fact is the
  // same one — this deployment states consequences the caller has not read —
  // and so is the recovery. There is no revision to carry, and the field is
  // absent from the envelope, which is what `null` says here.
  if (envelopeString(error, "field") === "acknowledgment.consequencesDigest") {
    return { kind: "contract_revised", currentContractRevision: null };
  }
  if (error.status === 400 || error.status === 422) {
    // The SERVER's own sentence: a 422 here is the typed validator explaining
    // which rule the request broke, and paraphrasing it would either lose that
    // or duplicate the rules.
    return {
      kind: "invalid",
      message: error.message,
      requestId: error.requestId,
    };
  }
  return { kind: "error", code: error.code, requestId: error.requestId };
}

/**
 * The §8 admission decision for one frozen candidate against the Project's
 * CURRENT preview target (#853).
 *
 * A read, never a derivation. The browser cannot observe a target's detector
 * policy and must not invent one, so the outcome — and the fixed statement
 * that a compatible posture is not a deployment approval — come from the
 * service that owns the rule.
 */
export function useCandidateRuntimeDetectionQuery(
  projectId: string,
  candidateId: string | null,
): UseQueryResult<CandidateRuntimeDetection> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.candidateRuntimeDetection(projectId, candidateId ?? ""),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET(
          /*
           * Suppressed for the reason `preview.useCandidateContentsQuery`
           * records at the only other call site that needs it, and on the same
           * terms: ADR 0001 forbids a candidate identifier in a BROWSER URL —
           * the address bar, a link, a route parameter — and this is an Editor
           * Service request path, where the contract puts `candidateId`
           * deliberately. `client.GET` takes the string as a TEMPLATE and
           * openapi-fetch substitutes `params.path`; nothing here reaches
           * `react-router`, `history` or an anchor, and `invariants/routes.test.ts`
           * still enforces the actual prohibition over the real route table.
           */
          // eslint-disable-next-line no-restricted-syntax
          "/v1/projects/{projectId}/preview-candidates/{candidateId}/runtime-detection",
          {
            // Non-null by the `enabled` guard below: TanStack Query never runs
            // a disabled query's `queryFn`, and the empty string in the key
            // above is only there because a key may not contain `null`.
            params: { path: { projectId, candidateId: candidateId ?? "" } },
            signal,
          },
        ),
      );
    },
    enabled: projectId.length > 0 && (candidateId ?? "").length > 0,
  });
}
